/**
 * The step-colour editor (2026-08-18).
 *
 * Item 5 of the review: *"colours should be pickable, and maybe pickable based on
 * steps (ex 0-10, 20-100) and customizables"*. This is the authoring half; the
 * resolution half is `Widgets/WebWidget/colorSteps.ts`, which owns the storage
 * shape and the band-matching rule.
 *
 * ## The author thinks in ranges; the storage is floors
 *
 * The stored shape is a list of `{ from, color }` FLOORS, not a list of ranges —
 * `colorSteps.ts` explains why (ranges can have gaps and overlaps, and need an
 * upper bound on the last band that the author has to predict). But "0 – 9" is how
 * a person reads a band, so that is what this component shows, computed from the
 * next floor by `colorStepRangeLabel`. One function derives the label the author
 * READS from the array the renderer SCANS, so the two can never disagree.
 *
 * The author therefore edits ONE number per band — where it starts — and the range
 * beside it updates itself. Editing "0 – 9" as two independent numbers is what
 * would let them produce `0-10` and `20-100` with 15 undefined, which is exactly
 * the state this shape makes unrepresentable.
 *
 * ## Sorting is deferred, deliberately
 *
 * `parseColorSteps` sorts on the way IN, and this editor keeps the author's rows in
 * the order they are typed until the value round-trips. Sorting on every keystroke
 * would yank a row out from under the cursor the moment someone typed "1" on the way
 * to "100" — the row would jump to the top, and the caret with it.
 */

import { ActionIcon, Button, ColorInput, Group, NumberInput, Stack, Text } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { colorStepRangeLabel, type ColorStep } from '../Widgets/WebWidget/colorSteps';
import { isCustomTint } from '../WidgetCard/tintTokens';
import { componentsTestIds } from '@components/testids';
import classes from './WidgetInspector.module.css';

export type ColorStepsEditorProps = {
  /** The raw stored value — untrusted, straight off `widgetComponentProps`. */
  value: unknown;
  /** Emits the whole list. `undefined` when the last band is removed. */
  onChange: ( steps: ColorStep[] | undefined ) => void;
};

/**
 * The starting bands offered by "add the first one".
 *
 * A blank first row would be a worse start than no rows: the author would have to
 * guess both the shape and the point. These three are the shape of nearly every
 * count alert — routine, worth a look, deal with it now — and they use the app's own
 * semantic hues rather than arbitrary colours so a default set matches the severity
 * palette a reader already knows.
 */
const DEFAULT_STEPS: ColorStep[] = [
  { from: 0, color: '#2f9e44' },
  { from: 10, color: '#f08c00' },
  { from: 50, color: '#e03131' }
];

/**
 * Read the stored value WITHOUT sorting or dropping.
 *
 * ⚠️ Deliberately NOT `parseColorSteps`. The renderer's parser drops malformed
 * entries, which is right for painting and wrong for editing: an author whose
 * half-typed colour vanished from the list as they typed it would have no way to
 * finish typing it. This keeps every row the author can see and lets the RENDERER
 * ignore the ones that are not yet valid — so an in-progress edit degrades to "that
 * band is not painting yet" rather than "that band disappeared".
 */
const readSteps = ( value: unknown ): ColorStep[] => {
  if ( !Array.isArray( value )) return [];
  return value
    .filter(( step ): step is Record<string, unknown> => typeof step === 'object' && step !== null )
    .map(( step ) => ({
      from: typeof step.from === 'number' && Number.isFinite( step.from ) ? step.from : 0,
      color: typeof step.color === 'string' ? step.color : ''
    }));
};

export function ColorStepsEditor({ value, onChange }: ColorStepsEditorProps ) {
  const { t } = useTranslation( 'widgets' );
  const steps = readSteps( value );

  const commit = ( next: ColorStep[]) => onChange( next.length ? next : undefined );

  const setAt = ( index: number, patch: Partial<ColorStep> ) =>
    commit( steps.map(( step, i ) => ( i === index ? { ...step, ...patch } : step )));

  const removeAt = ( index: number ) => commit( steps.filter(( _, i ) => i !== index ));

  const append = () => {
    /*
     * The new band starts ABOVE the highest one, because appending below or equal to
     * an existing floor produces a band with no values in it — invisible, and
     * indistinguishable from a bug. `+10` is a guess at a useful gap, and it is only
     * a starting value: the author types over it.
     */
    const highest = steps.reduce(( max, step ) => Math.max( max, step.from ), 0 );
    commit([ ...steps, { from: highest + 10, color: '#868e96' }]);
  };

  if ( steps.length === 0 ) {
    return (
      <Stack gap="xxs">
        <Text className={classes.hint}>
          {t(
            'widget_inspector_color_steps_empty',
            'Sin tramos, la tarjeta usa el color de la severidad. Podés pintar el número según cuánto marque.'
          )}
        </Text>
        <Button
          size="xs"
          radius="xs"
          variant="light"
          leftSection={<IconPlus size={14} />}
          data-testid={componentsTestIds.reportsInspectorColorStepsAdd}
          onClick={() => commit( DEFAULT_STEPS )}
        >
          {t( 'widget_inspector_color_steps_start', 'Usar tramos de color' )}
        </Button>
      </Stack>
    );
  }

  // Sorted COPY, for the range labels only — see the header note on why the rows
  // themselves keep their authored order.
  const sorted = [...steps].sort(( a, b ) => a.from - b.from );

  return (
    <Stack gap="xxs">
      {steps.map(( step, index ) => {
        const rangeIndex = sorted.findIndex(( s ) => s === step );
        return (
          <Group key={index} gap="xxs" wrap="nowrap" align="center">
            <NumberInput
              size="xs"
              w={72}
              data-testid={componentsTestIds.reportsInspectorColorStepFrom( index )}
              aria-label={t( 'widget_inspector_color_steps_from', 'Desde' )}
              value={step.from}
              onChange={( next ) => setAt( index, { from: typeof next === 'number' ? next : 0 })}
            />
            <Text className={classes.hint} style={{ flex: '0 0 auto', minWidth: 56 }}>
              {colorStepRangeLabel( sorted, rangeIndex )}
            </Text>
            <ColorInput
              size="xs"
              format="hexa"
              withEyeDropper
              style={{ flex: 1, minWidth: 0 }}
              data-testid={componentsTestIds.reportsInspectorColorStepColor( index )}
              aria-label={t( 'widget_inspector_color_steps_color', 'Color del tramo' )}
              value={isCustomTint( step.color ) ? step.color : ''}
              onChange={( next ) => setAt( index, { color: next })}
            />
            <ActionIcon
              size="sm"
              variant="subtle"
              color="gray"
              data-testid={componentsTestIds.reportsInspectorColorStepRemove( index )}
              aria-label={t( 'widget_inspector_color_steps_remove', 'Quitar tramo' )}
              onClick={() => removeAt( index )}
            >
              <IconTrash size={14} />
            </ActionIcon>
          </Group>
        );
      })}

      <Group gap="xxs" justify="space-between" wrap="nowrap">
        <Button
          size="xs"
          radius="xs"
          variant="subtle"
          leftSection={<IconPlus size={14} />}
          data-testid={componentsTestIds.reportsInspectorColorStepsAdd}
          onClick={append}
        >
          {t( 'widget_inspector_color_steps_add', 'Agregar tramo' )}
        </Button>
      </Group>

      <Text className={classes.hint}>
        {/*
          Both sentences are load-bearing and neither is obvious from the UI.

          The first names the precedence — a matching band beats the severity — which
          is otherwise only discoverable by setting both and seeing which won.

          The second is the one that saves a support ticket: BELOW the lowest floor
          nothing matches, on purpose (`resolveStepColor`), so a card whose first band
          starts at 10 shows the severity colour at 3. Without this line that reads as
          the feature being broken.
        */}
        {t(
          'widget_inspector_color_steps_hint',
          'Cada tramo empieza en el número que indicás y termina donde arranca el siguiente. Si el valor queda por debajo del primer tramo, la tarjeta usa el color de la severidad.'
        )}
      </Text>
    </Stack>
  );
}
